// cleanup_remove_external_libraries_PAYMENT_MAPS
// cleanup_convert_to_typescript_file_PAYMENT_MAPS

import React, { useCallback, useEffect, useMemo } from 'react';
import classnames from 'classnames';
import { get, pick, template } from 'lodash';

import { WholeNumber as NumberInput } from '@glu/input-mask-react';
import { ValidatedInput as Input } from '@glu/form-components';

import useLocale from 'components/useLocale';

import { DROPDOWN_OPTION_NONE, NONE, POSITION_FIELD_MAX_LENGTH } from '../constants';
import DelimitedDropdown from '../DelimitedDropdown';
import useStyles from '../Table.styles';
import SharedRowPart from '../SharedRowPart';

import {
  getFixedFormatTableFields,
  IMPORT_FILE_MAX_LENGTH,
  LENGTH_MAX_LENGTH,
  MAP_FORMAT,
  START_POSITION_MAX_LENGTH
} from '../utils.mapFormats';
import {
  asyncExecuteMultiplyGluFunctions,
  validateDuplicatePositionField,
  validateGraterThanOne,
  validateGraterThanZero,
  validateLength,
  validateUnique,
  clearComponent,
  endingPositionGreaterRecordLengthValidation,
  overlapValidation,
  startingPositionGreaterRecordLengthValidation,
  requiredValidator,
  asyncRegisterMultiplyGluValidators
} from '../utils';
import { PROCESS_MODES_NAMES } from '../../utils';

const getImportFileInputs = (field) =>
  field?.dropdownData?.reduce(
    (acc, option) => (option.id !== NONE ? [...acc, option.id] : [...acc]),
    []
  ) || [];

export const TransactionTypesRowComponent = ({
  cellClass,
  changeTransactionFieldVariant,
  field,
  formState,
  handlers,
  isModeUpdateOnly,
  mapFormat,
  onChange,
  onPositionFieldChange,
  processMode,
  registerValidators,
  sending,
  transactionFieldVariant,
  turnOnCheckboxes,
  unregisterValidators,
  validate
}) => {
  const locale = useLocale();
  const classes = useStyles();

  const { handleFixedAddValueFieldFocus, onFixedFieldFocus } = handlers.fixed;

  const importFileInputs = useMemo(() => getImportFileInputs(field), [field]);

  const isPositionFieldDisabled = useMemo(
    () =>
      sending ||
      (formState[`${field.id}_addValue`] &&
        get(formState[`${field.id}_addValue`], '[0].id') !== NONE),
    [field, sending, formState]
  );

  const isPositionAvailable = useMemo(
    () =>
      mapFormat === MAP_FORMAT.DELIMITED
        ? formState[field.id]
        : formState[`${field.id}_startPosition`] || formState[`${field.id}_length`],
    [field, mapFormat, formState]
  );

  const isAddValueDisabled = useMemo(
    () => sending || isPositionAvailable || importFileInputs.some((input) => formState[input]),
    [sending, importFileInputs, formState, isPositionAvailable]
  );

  const calculateFieldLength = useCallback(() => {
    const importFileInputsValues = importFileInputs
      .map((input) => formState[input]?.length)
      .filter(Boolean);
    const maxValue = Math.max(...importFileInputsValues);

    onChange(`${field.id}_length`, importFileInputsValues.length ? maxValue.toString() : '');
  }, [importFileInputs, onChange, formState, field]);

  const importFileValidation = useMemo(
    () => [
      validateGraterThanZero(locale),
      validateLength(IMPORT_FILE_MAX_LENGTH, locale),
      validateUnique(locale, importFileInputs)
    ],
    [locale, importFileInputs]
  );

  const positionValidation = useMemo(
    () => [
      validateGraterThanOne(locale),
      validateLength(POSITION_FIELD_MAX_LENGTH, locale),
      validateDuplicatePositionField(locale)
    ],
    [locale]
  );

  const lengthFieldValidators = useMemo(
    () => [
      validateGraterThanOne(locale),
      validateLength(LENGTH_MAX_LENGTH, locale),
      endingPositionGreaterRecordLengthValidation(locale),
      overlapValidation(locale)
    ],
    [locale]
  );

  const startPositionFieldValidators = useMemo(() => {
    const isValue =
      isModeUpdateOnly &&
      mapFormat === MAP_FORMAT.FIXED &&
      importFileInputs.some((input) => formState[input]);
    return [
      validateGraterThanOne(locale),
      validateLength(START_POSITION_MAX_LENGTH, locale),
      startingPositionGreaterRecordLengthValidation(locale),
      overlapValidation(locale),
      ...(!isModeUpdateOnly || isValue ? [requiredValidator(locale)] : [])
    ];
  }, [locale, isModeUpdateOnly, formState, importFileInputs, mapFormat]);

  const onImportFileInputBlur = useCallback(async () => {
    const tableFields = getFixedFormatTableFields(locale, true, true);

    const pickFieldsData = pick(formState, tableFields);

    const arrayOfPositionNames = Object.keys(pickFieldsData)
      .map((key) => (pickFieldsData[key] ? key : null))
      .filter(Boolean);

    const lengthField = formState[`${field.id}_startPosition`] ? [`${field.id}_length`] : [];
    const positionsAndImportsToValidate = [
      ...importFileInputs,
      ...arrayOfPositionNames,
      ...lengthField
    ];

    if (mapFormat === MAP_FORMAT.FIXED) {
      calculateFieldLength();
    }

    await asyncExecuteMultiplyGluFunctions(positionsAndImportsToValidate, validate);

    // for UPDATE_ONLY map
    if (processMode === PROCESS_MODES_NAMES.UPDATE_ONLY && mapFormat === MAP_FORMAT.FIXED) {
      const hasAtLeastOneValue = importFileInputs.some((input) => formState[input]);
      if (hasAtLeastOneValue) {
        await registerValidators(`${field.id}_startPosition`, [requiredValidator(locale)]);
      } else {
        await unregisterValidators(`${field.id}_startPosition`);
      }
    }
    return null;
  }, [
    importFileInputs,
    validate,
    mapFormat,
    calculateFieldLength,
    formState,
    locale,
    field,
    processMode,
    registerValidators,
    unregisterValidators
  ]);

  const onFixedPositionFieldBlur = useCallback(
    async (name) => {
      if (formState[name]) {
        await registerValidators(`${field.id}_length`, [
          requiredValidator(locale, locale['wireImports.valueInImportFileCannotBeBlank'])
        ]);
        return null;
      }
      await unregisterValidators(`${field.id}_length`);
      await registerValidators(`${field.id}_length`, lengthFieldValidators);
      return null;
    },
    [field, formState, locale, registerValidators, unregisterValidators, lengthFieldValidators]
  );

  const mountValidation = useCallback(() => {
    const inputsToMount = importFileInputs.map((input) => ({
      name: input,
      validator: importFileValidation
    }));

    const dropdownAddValue = get(formState[`${field.id}_addValue`], '[0].id', null);

    const fieldsToMount = [
      ...inputsToMount,
      ...(mapFormat === MAP_FORMAT.DELIMITED
        ? [
            {
              name: field.id,
              validator: positionValidation
            }
          ]
        : []),
      ...(mapFormat === MAP_FORMAT.FIXED
        ? [
            {
              name: `${field.id}_startPosition`,
              validator:
                !dropdownAddValue || dropdownAddValue === NONE
                  ? startPositionFieldValidators
                  : null
            },
            {
              name: `${field.id}_length`,
              validator:
                mapFormat === MAP_FORMAT.FIXED && formState[`${field.id}_startPosition`]
                  ? [
                      ...lengthFieldValidators,
                      requiredValidator(
                        locale,
                        locale['wireImports.valueInImportFileCannotBeBlank']
                      )
                    ]
                  : lengthFieldValidators
            }
          ]
        : [])
    ];

    asyncRegisterMultiplyGluValidators(fieldsToMount, registerValidators);
  }, [
    registerValidators,
    importFileInputs,
    importFileValidation,
    positionValidation,
    startPositionFieldValidators,
    lengthFieldValidators,
    locale,
    field,
    mapFormat,
    formState
  ]);

  useEffect(() => {
    mountValidation();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mountValidation]);
  useEffect(
    () => () => {
      clearComponent({
        field,
        importFileInputs,
        onChange,
        processMode,
        unregisterValidators
      });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  return (
    <div className={classes.row}>
      <SharedRowPart
        cellClass={classes.transactionField}
        changeTransactionFieldVariant={changeTransactionFieldVariant}
        field={field}
        fieldNameClassName={cellClass}
        formState={formState}
        onChange={onChange}
        processMode={processMode}
        sending={sending}
        transactionFieldVariant={transactionFieldVariant}
        turnOnCheckboxes={turnOnCheckboxes}
      />

      {mapFormat === MAP_FORMAT.DELIMITED ? (
        <div className={classnames(cellClass, classes.positionField, classes.transactionField)}>
          <NumberInput
            data-qa={field.id}
            disabled={isPositionFieldDisabled}
            htmlName={field.id}
            maxLengthIndicatorLimit={POSITION_FIELD_MAX_LENGTH}
            name={field.id}
            onChange={onPositionFieldChange}
            suffixSymbol={null}
          />
        </div>
      ) : (
        <>
          <div className={classnames(cellClass, classes.positionField, classes.transactionField)}>
            <NumberInput
              data-qa={`${field.id}_startPosition`}
              htmlName={`${field.id}_startPosition`}
              name={`${field.id}_startPosition`}
              {...(mapFormat === MAP_FORMAT.FIXED ? { onBlur: onFixedPositionFieldBlur } : {})}
              disabled={isPositionFieldDisabled}
              maxLengthIndicatorLimit={START_POSITION_MAX_LENGTH}
              onChange={onPositionFieldChange}
              onFocus={onFixedFieldFocus}
              suffixSymbol={null}
            />
          </div>
          <div className={classnames(cellClass, classes.positionField, classes.transactionField)}>
            <NumberInput
              data-qa={`${field.id}_length`}
              disabled
              formatValueFromProps
              htmlName={`${field.id}_length`}
              maxLengthIndicatorLimit={LENGTH_MAX_LENGTH}
              name={`${field.id}_length`}
              onChange={onPositionFieldChange}
              onFocus={onFixedFieldFocus}
              suffixSymbol={null}
            />
          </div>
        </>
      )}

      <div className={classnames(cellClass)}>
        {importFileInputs.map((input) => (
          <Input
            key={`fileInput-${input}`}
            data-qa={input}
            disabled={isPositionFieldDisabled}
            htmlName={input}
            labelText={locale[`wireImports.${input}`]}
            maxLengthIndicatorLimit={IMPORT_FILE_MAX_LENGTH}
            name={input}
            onBlur={onImportFileInputBlur}
          />
        ))}
      </div>
      <div className={classnames(cellClass, classes.addValueField, classes.transactionField)}>
        <DelimitedDropdown
          className={classnames(classes.dropdown)}
          dataQa={`${field.id}_addValue`}
          labelText={
            turnOnCheckboxes
              ? template(locale['wireImports.replacementValue.field'])({ fieldName: field?.name })
              : template(locale['wireImports.addValue.field'])({ fieldName: field?.name })
          }
          {...(!formState[`${field.id}_addValue`]?.length
            ? { defaultValue: [DROPDOWN_OPTION_NONE] }
            : {})}
          disabled={isAddValueDisabled}
          name={`${field.id}_addValue`}
          onChange={() => {
            const isNoneOptionSelected = get(formState[`${field.id}_addValue`], '[0].id') === NONE;

            if (!formState[`${field.id}_addValue`] || isNoneOptionSelected) {
              if (mapFormat === MAP_FORMAT.FIXED && !isModeUpdateOnly) {
                return handleFixedAddValueFieldFocus(field);
              }
            }
            return null;
          }}
          options={field?.dropdownData}
        />
      </div>
    </div>
  );
};

export default TransactionTypesRowComponent;
